<template>
    <div class="quick-add-child" :class="{ 'form-open': showForm }">
        <!-- Toggle Button -->
        <button
            type="button"
            class="btn-add-campaign"
            @click="showForm = !showForm"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {{ showForm ? 'Cancel' : 'Add Supporting Campaign' }}
        </button>

        <!-- Expandable Form -->
        <transition name="slide">
            <div v-if="showForm" class="quick-add-form">
                <!-- Success Message -->
                <div v-if="successMessage" class="alert alert-success">
                    <svg xmlns="http://www.w3.org/2000/svg" class="alert-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ successMessage }}
                </div>

                <!-- Error Message -->
                <div v-if="errorMessage" class="alert alert-error">
                    <svg xmlns="http://www.w3.org/2000/svg" class="alert-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ errorMessage }}
                </div>

                <form @submit.prevent="submitForm">
                    <div class="form-grid">
                        <!-- Campaign Name -->
                        <div class="form-group">
                            <label for="qa-name">Campaign Name <span class="required">*</span></label>
                            <input
                                id="qa-name"
                                v-model="form.name"
                                type="text"
                                class="form-input"
                                placeholder="e.g., Bulldogs Dig Pink"
                                required
                                maxlength="250"
                                ref="nameInput"
                            />
                        </div>

                        <!-- Organization Name -->
                        <div class="form-group">
                            <label for="qa-business">Organization Name <span class="required">*</span></label>
                            <input
                                id="qa-business"
                                v-model="form.business_name"
                                type="text"
                                class="form-input"
                                placeholder="e.g., Westminster High School"
                                required
                                maxlength="250"
                            />
                        </div>

                        <!-- Goal -->
                        <div class="form-group">
                            <label for="qa-goal">Fundraising Goal ($)</label>
                            <input
                                id="qa-goal"
                                v-model="form.goal"
                                type="number"
                                class="form-input"
                                :placeholder="'Default: $' + defaultGoal"
                                min="100"
                                max="1000000"
                            />
                        </div>

                        <!-- Event Date -->
                        <div class="form-group">
                            <label for="qa-event-date">Event Date</label>
                            <input
                                id="qa-event-date"
                                v-model="form.event_date"
                                type="date"
                                class="form-input"
                            />
                        </div>

                        <!-- Campaign Type -->
                        <div class="form-group">
                            <label for="qa-type">Campaign Type</label>
                            <select
                                id="qa-type"
                                v-model="form.campaign_type"
                                class="form-input"
                            >
                                <option value="">Use parent's type</option>
                                <option value="school">School</option>
                                <option value="club">Club</option>
                                <option value="business">Business</option>
                                <option value="tournament">Tournament</option>
                                <option value="individual">Individual</option>
                            </select>
                        </div>

                        <!-- City & State inline -->
                        <div class="form-group form-group-inline">
                            <div class="inline-field">
                                <label for="qa-city">City</label>
                                <input
                                    id="qa-city"
                                    v-model="form.city"
                                    type="text"
                                    class="form-input"
                                    :placeholder="parentCity || 'City'"
                                    maxlength="250"
                                />
                            </div>
                            <div class="inline-field inline-field-small">
                                <label for="qa-state">State</label>
                                <input
                                    id="qa-state"
                                    v-model="form.state"
                                    type="text"
                                    class="form-input"
                                    :placeholder="parentState || 'ST'"
                                    maxlength="2"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="form-actions">
                        <button
                            type="submit"
                            class="btn-submit"
                            :disabled="submitting"
                        >
                            <svg v-if="submitting" class="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25"></circle>
                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" opacity="0.75"></path>
                            </svg>
                            {{ submitting ? 'Creating...' : 'Create Campaign' }}
                        </button>
                        <span class="campaigns-created" v-if="createdCount > 0">
                            {{ createdCount }} campaign{{ createdCount !== 1 ? 's' : '' }} created this session
                        </span>
                    </div>
                </form>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'QuickAddChild',

    props: {
        parentId: {
            type: [Number, String],
            required: true,
        },
        defaultGoal: {
            type: [Number, String],
            default: 2500,
        },
        parentCity: {
            type: String,
            default: '',
        },
        parentState: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            showForm: false,
            submitting: false,
            successMessage: '',
            errorMessage: '',
            createdCount: 0,
            form: {
                name: '',
                business_name: '',
                goal: '',
                event_date: '',
                campaign_type: '',
                city: '',
                state: '',
            },
        };
    },

    methods: {
        async submitForm() {
            this.submitting = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {
                const payload = { ...this.form };

                // Remove empty optional fields so server uses defaults
                Object.keys(payload).forEach(key => {
                    if (payload[key] === '' || payload[key] === null) {
                        delete payload[key];
                    }
                });

                const response = await axios.post(
                    `/campaign/${this.parentId}/quick-add-child`,
                    payload
                );

                if (response.data.success) {
                    this.createdCount++;
                    this.successMessage = response.data.message;

                    // Emit event for parent page to refresh campaign list
                    this.$emit('campaign-created', response.data.campaign);

                    // Clear form for "Add Another"
                    this.resetForm();

                    // Focus name input for rapid entry
                    this.$nextTick(() => {
                        if (this.$refs.nameInput) {
                            this.$refs.nameInput.focus();
                        }
                    });

                    // Clear success message after 5 seconds
                    setTimeout(() => {
                        this.successMessage = '';
                    }, 5000);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 409) {
                        this.errorMessage = error.response.data.message || 'A campaign with this name already exists.';
                    } else if (error.response.status === 422 && error.response.data.errors) {
                        const firstError = Object.values(error.response.data.errors)[0][0];
                        this.errorMessage = firstError;
                    } else {
                        this.errorMessage = error.response.data.message || 'Failed to create campaign.';
                    }
                } else {
                    this.errorMessage = 'Network error. Please try again.';
                }

                setTimeout(() => {
                    this.errorMessage = '';
                }, 7000);
            } finally {
                this.submitting = false;
            }
        },

        resetForm() {
            this.form.name = '';
            this.form.business_name = '';
            this.form.goal = '';
            this.form.event_date = '';
            // Keep campaign_type, city, state — likely same for all children in a batch
        },
    },
};
</script>

<style scoped>
.quick-add-child {
    position: relative;
}

.quick-add-form {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    margin-top: 0.75rem;
    padding: 1.5rem;
    background: #FAFAFA;
    border: 1px solid #E5E7EB;
    border-radius: 0.75rem;
    width: max(500px, 60vw);
    max-width: 900px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.btn-add-campaign {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    background: linear-gradient(135deg, #EC4899, #DB2777);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-add-campaign:hover {
    background: linear-gradient(135deg, #DB2777, #BE185D);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-add-campaign .icon {
    width: 1.25rem;
    height: 1.25rem;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

@media (max-width: 640px) {
    .form-grid {
        grid-template-columns: 1fr;
    }
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
}

.required {
    color: #EF4444;
}

.form-input {
    padding: 0.5rem 0.75rem;
    border: 1.5px solid #D1D5DB;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: border-color 0.2s;
    width: 100%;
    box-sizing: border-box;
}

.form-input:focus {
    border-color: #EC4899;
    outline: none;
    box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
}

.form-group-inline {
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
}

.inline-field {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.inline-field-small {
    flex: 0 0 5rem;
}

.form-actions {
    margin-top: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.btn-submit {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.5rem;
    background: #97d700;
    color: #1a1a1a;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-submit:hover:not(:disabled) {
    background: #85c000;
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.spinner {
    animation: spin 1s linear infinite;
    width: 1rem;
    height: 1rem;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.campaigns-created {
    font-size: 0.8125rem;
    color: #059669;
    font-weight: 500;
}

.alert {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
}

.alert-success {
    background-color: #D1FAE5;
    border: 1px solid #6EE7B7;
    color: #065F46;
}

.alert-error {
    background-color: #FEE2E2;
    border: 1px solid #FCA5A5;
    color: #991B1B;
}

.alert-icon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
}

/* Transition */
.slide-enter-active,
.slide-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-0.5rem);
}
</style>
